/*
 * Rules for the help chat ("ProCharted help" panel): reading the assistant's
 * markdown-lite reply, trimming the history sent to the server, the chat
 * excerpt attached to a problem report, and the page address (without its
 * query string / #, which can hold sign-in tokens).
 */

import { PROC_VERSION } from "../../../app/configs/appConfig";
import { AiHelpChatMessage, AiHelpLink } from "../../../slices/AiSlice";

export type HelpMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  links?: AiHelpLink[];
  suggestReport?: boolean;
};

/** At most this many messages go to the server. */
export const MAX_HISTORY = 20;
export const EXCERPT_MESSAGES = 6;
export const EXCERPT_MAX_CHARS = 4000;
export const STORAGE_KEY = "proc_help_chat";

/** Report a problem: the server's limits (whatHappened 4,000, expected 2,000; QA batch 13). */
export const REPORT_WHAT_MAX = 4000;
export const REPORT_EXPECTED_MAX = 2000;

/** "123 characters left" under a box; shown from 80% of the limit. "" before that. */
export const charactersLeftText = (value: string, max: number): string => {
  const left = max - value.length;
  if (value.length < max * 0.8) return "";
  return left <= 0
    ? "Limit reached"
    : `${left.toLocaleString("en-US")} character${left === 1 ? "" : "s"} left`;
};

export const SUGGESTED_QUESTIONS = [
  "How do I add a client?",
  "How do I set up recurring work?",
  "How do I track time?",
  "How do I give someone Settings access?",
];

// ---------- Markdown-lite ----------

export type InlinePart = { text: string; bold: boolean };
export type HelpBlock =
  | { type: "p"; parts: InlinePart[] }
  | { type: "ol"; start: number; items: InlinePart[][] }
  | { type: "ul"; items: InlinePart[][] };

/**
 * "Click **Save** now" -> [{Click }, {Save, bold}, { now}]. Everything stays
 * text (React escapes it); markdown links "[label](url)" keep only the label,
 * and HTML tags are removed.
 */
export const parseInline = (line: string): InlinePart[] => {
  const cleaned = line
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/?[a-z][^>]*>/gi, "")
    .replace(/\[([^\]]+)\]\((?:[^()]|\([^()]*\))*\)/g, "$1")
    .replace(/`([^`]+)`/g, "$1");
  const parts: InlinePart[] = [];
  const pattern = /\*\*(.+?)\*\*|__(.+?)__/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(cleaned))) {
    if (match.index > last) {
      parts.push({ text: cleaned.slice(last, match.index), bold: false });
    }
    parts.push({ text: match[1] ?? match[2], bold: true });
    last = match.index + match[0].length;
  }
  if (last < cleaned.length) {
    parts.push({ text: cleaned.slice(last), bold: false });
  }
  // A stray "**" that wasn't closed.
  return parts
    .map((part) => ({ ...part, text: part.text.replace(/\*\*/g, "") }))
    .filter((part) => part.text);
};

const NUMBERED = /^\s*(\d+)[.)]\s+/;
const BULLETED = /^\s*[-*•–]\s+/;
const HEADING = /^\s*#{1,6}\s+/;

/**
 * The assistant's reply -> paragraphs, numbered steps and bullet lists.
 * Lines in a paragraph are joined with a space; a "# Heading" becomes a bold
 * paragraph.
 */
export const parseHelpReply = (text: string): HelpBlock[] => {
  const blocks: HelpBlock[] = [];
  let paragraph: string[] = [];
  const flushParagraph = () => {
    const joined = paragraph.join(" ").trim();
    if (joined) blocks.push({ type: "p", parts: parseInline(joined) });
    paragraph = [];
  };
  const lastBlock = () => blocks[blocks.length - 1];

  (text || "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .forEach((raw) => {
      const line = raw.trim();
      if (!line) {
        flushParagraph();
        return;
      }
      const numbered = NUMBERED.exec(line);
      if (numbered) {
        flushParagraph();
        const item = parseInline(line.replace(NUMBERED, ""));
        const block = lastBlock();
        if (block?.type === "ol") block.items.push(item);
        else
          blocks.push({
            type: "ol",
            start: Number(numbered[1]),
            items: [item],
          });
        return;
      }
      if (BULLETED.test(line)) {
        flushParagraph();
        const item = parseInline(line.replace(BULLETED, ""));
        const block = lastBlock();
        if (block?.type === "ul") block.items.push(item);
        else blocks.push({ type: "ul", items: [item] });
        return;
      }
      if (HEADING.test(line)) {
        flushParagraph();
        const title = line.replace(HEADING, "").replace(/\*\*/g, "").trim();
        if (title)
          blocks.push({ type: "p", parts: [{ text: title, bold: true }] });
        return;
      }
      paragraph.push(line);
    });
  flushParagraph();

  // A blank line between two "1." steps shouldn't restart the list: blocks
  // are already merged because only blank lines flush paragraphs.
  return blocks.filter((block) => block.type !== "p" || block.parts.length);
};

/** Only http(s) links with a title; others are dropped. */
export const safeLinks = (links?: AiHelpLink[] | null): AiHelpLink[] =>
  (links || []).filter((link) => {
    if (!link || typeof link.url !== "string") return false;
    try {
      const url = new URL(link.url);
      return url.protocol === "https:" || url.protocol === "http:";
    } catch {
      return false;
    }
  });

// ---------- History / excerpt ----------

/**
 * The messages sent to the server: the last `max`, as {role, text}, starting
 * with a question from the person (a leading answer is dropped).
 */
export const trimHistory = (
  messages: HelpMessage[],
  max: number = MAX_HISTORY,
): AiHelpChatMessage[] => {
  const recent = messages
    .filter((message) => message.text.trim())
    .slice(-max)
    .map(({ role, text }) => ({ role, text }));
  while (recent.length && recent[0].role !== "user") recent.shift();
  return recent;
};

/**
 * The end of the chat for a problem report: "You: …" / "Assistant: …", the
 * last `count` messages, at most `maxChars` characters (oldest text is cut).
 */
export const buildChatExcerpt = (
  messages: HelpMessage[],
  count: number = EXCERPT_MESSAGES,
  maxChars: number = EXCERPT_MAX_CHARS,
): string => {
  const text = messages
    .filter((message) => message.text.trim())
    .slice(-count)
    .map(
      (message) =>
        `${
          message.role === "user" ? "You" : "Assistant"
        }: ${message.text.trim()}`,
    )
    .join("\n\n");
  if (text.length <= maxChars) return text;
  return `…${text.slice(text.length - (maxChars - 1))}`;
};

// ---------- Page, browser, version ----------

/** The page address without query string and # (they can hold tokens). */
export const redactPageUrl = (href: string): string => {
  if (!href) return "";
  try {
    const url = new URL(href);
    return `${url.origin}${url.pathname}`;
  } catch {
    return href.split(/[?#]/)[0];
  }
};

/**
 * Version of the web app: REACT_APP_VERSION if a build sets it, otherwise
 * PROC_VERSION (the version App Insights reports).
 */
export const appVersion = (): string =>
  process.env.REACT_APP_VERSION || `web ${PROC_VERSION}`;

export const browserInfo = (): string =>
  typeof navigator !== "undefined" ? navigator.userAgent : "";

// ---------- Saved conversation (this browser tab only) ----------

export const loadMessages = (): HelpMessage[] => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (message): message is HelpMessage =>
        !!message &&
        (message.role === "user" || message.role === "assistant") &&
        typeof message.text === "string" &&
        typeof message.id === "string",
    );
  } catch {
    return [];
  }
};

export const saveMessages = (messages: HelpMessage[]) => {
  try {
    if (messages.length) {
      // Keep the saved chat small.
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-60)));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage blocked or full: the chat just isn't kept.
  }
};

let counter = 0;
export const newMessageId = () => {
  counter += 1;
  return `${Date.now().toString(36)}-${counter}`;
};
